import React, { useState, useEffect } from 'react';
import { Lead } from '../types';
import { X, MessageSquare, Mail, Phone, Calendar, Tag, Globe, Clock, History } from 'lucide-react';
import { LeadTimelineModal } from './LeadTimelineModal';
import { supabase } from '../integrations/supabase/client';
import { useToast } from '../hooks/use-toast';

interface TimelineEntry {
  id: string;
  titulo: string;
  descricao: string | null;
  tipo: string;
  criado_por: string | null;
  created_at: string;
  metadata: any;
}

interface PropertyInterest {
  property_id: string;
  property_title?: string;
  transaction_type?: string;
  created_at: string;
  source: string;
}

interface SimpleLeadDetailModalProps {
  lead: Lead | null;
  isOpen: boolean;
  onClose: () => void;
}

const SimpleLeadDetailModal: React.FC<SimpleLeadDetailModalProps> = ({ lead, isOpen, onClose }) => {
  const { toast } = useToast();
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineEntries, setTimelineEntries] = useState<TimelineEntry[]>([]);
  const [propertyInterests, setPropertyInterests] = useState<PropertyInterest[]>([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [originalLeadId, setOriginalLeadId] = useState<string | null>(null);
  
  if (!isOpen || !lead) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      case 'qualified': return 'bg-purple-100 text-purple-800';
      case 'converted': return 'bg-green-100 text-green-800';
      case 'lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'website': return 'bg-blue-100 text-blue-800';
      case 'olx': return 'bg-orange-100 text-orange-800';
      case 'whatsapp': return 'bg-green-100 text-green-800';
      case 'facebook': return 'bg-blue-100 text-blue-800';
      case 'instagram': return 'bg-pink-100 text-pink-800';
      case 'manual': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const translateStatus = (status: string) => {
    switch (status) {
      case 'new': return 'Novo';
      case 'contacted': return 'Contatado';
      case 'qualified': return 'Qualificado';
      case 'converted': return 'Convertido';
      case 'lost': return 'Perdido';
      default: return status;
    }
  };

  const translateSource = (source: string) => {
    switch (source) {
      case 'website': return 'Site';
      case 'olx': return 'OLX';
      case 'whatsapp': return 'WhatsApp';
      case 'facebook': return 'Facebook';
      case 'instagram': return 'Instagram';
      case 'manual': return 'Manual';
      default: return source;
    }
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'website': return '🌐';
      case 'olx': return '🏠';
      case 'whatsapp': return '💬';
      case 'facebook': return '📘';
      case 'instagram': return '📷';
      case 'manual': return '✍️';
      default: return '📝';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('pt-BR'),
      time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    };
  };

  // Carregar timeline quando o modal abrir
  useEffect(() => {
    console.log('🚀 SimpleLeadDetailModal mounted with lead:', lead);
    if (isOpen && lead) {
      loadOriginalLeadId();
      loadTimelineData();
      loadPropertyInterests();
    }
  }, [isOpen, lead.id]);

  // Buscar o ID do lead original se este for um cliente convertido
  const loadOriginalLeadId = async () => {
    try {
      // Verificar se é um cliente convertido
      const { data: clientData } = await supabase
        .from('clients')
        .select('converted_from_lead_id, converted_from_portal_lead_id')
        .eq('id', lead.id)
        .maybeSingle();
      
      if (clientData?.converted_from_lead_id) {
        setOriginalLeadId(clientData.converted_from_lead_id);
      } else if (clientData?.converted_from_portal_lead_id) {
        setOriginalLeadId(clientData.converted_from_portal_lead_id);
      } else {
        setOriginalLeadId(lead.id); // É um lead original
      }
    } catch (error) {
      console.error('Erro ao buscar lead original:', error);
      setOriginalLeadId(lead.id); // Fallback para o ID atual
    }
  };

  const loadTimelineData = async () => {
    setLoadingTimeline(true);
    try {
      console.log('🔍 Loading timeline for lead ID:', lead.id);

      // 1) Tentar timeline diretamente pelo id atual (leads_all)
      const { data: tlDirect, error: tlErr } = await supabase
        .from('timeline_leads')
        .select('*')
        .eq('lead_id', lead.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (tlErr) throw tlErr;

      let timelineData = tlDirect || [];

      // 2) Se vazio, tentar timeline via leads_portais vinculados por email/telefone
      if (timelineData.length === 0) {
        const portalIds = new Set<string>();
        if (lead.email) {
          const { data: lpEmail } = await supabase
            .from('leads_portais')
            .select('id')
            .eq('email', lead.email)
            .order('created_at', { ascending: false })
            .limit(10);
          lpEmail?.forEach(r => portalIds.add(r.id));
        }
        if (lead.phone) {
          const { data: lpPhone } = await supabase
            .from('leads_portais')
            .select('id')
            .eq('phone', lead.phone)
            .order('created_at', { ascending: false })
            .limit(10);
          lpPhone?.forEach(r => portalIds.add(r.id));
        }

        if (portalIds.size > 0) {
          const { data: tlPortal } = await supabase
            .from('timeline_leads')
            .select('*')
            .in('lead_id', Array.from(portalIds))
            .order('created_at', { ascending: false })
            .limit(20);

          if (tlPortal && tlPortal.length > 0) {
            timelineData = tlPortal;
          }
        }
      }

      // 3) Se ainda vazio, criar entrada de fallback
      if (timelineData.length === 0) {
        timelineData = [{
          id: 'lead-creation',
          lead_id: lead.id,
          titulo: 'Lead criado',
          descricao: `Lead criado via ${lead.source}`,
          tipo: 'criacao',
          criado_por: 'Sistema',
          created_at: (lead as any).created_at || new Date().toISOString(),
          metadata: { source: lead.source }
        }];
      }

      // Deduplicate timeline entries by title + created_at (same minute)
      const seen = new Set<string>();
      const uniqueTimeline = timelineData.filter(entry => {
        const key = `${entry.titulo}-${entry.created_at?.substring(0, 16)}`; // same to minute
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      console.log('📋 Final timeline data:', uniqueTimeline);
      setTimelineEntries(uniqueTimeline);
    } catch (error) {
      console.error('Erro ao carregar timeline:', error);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const loadPropertyInterests = async () => {
    try {
      console.log('🔍 Loading property interests for lead:', lead);

      const propertyIds = new Set<string>();
      const interests: PropertyInterest[] = [];

      // 1) Tentar identificar imóvel diretamente no objeto lead
      const currentPropertyId = (lead as any).property_listing_id || (lead as any).property_id || lead.propertyId || (lead as any).client_listing_id;
      console.log('🏠 Current property ID found:', currentPropertyId, 'from lead:', {
        property_listing_id: (lead as any).property_listing_id,
        property_id: (lead as any).property_id,
        propertyId: lead.propertyId,
        client_listing_id: (lead as any).client_listing_id
      });
      if (currentPropertyId && !propertyIds.has(currentPropertyId)) {
        propertyIds.add(currentPropertyId);
        const { data: propertyData } = await supabase
          .from('properties')
          .select('title, transaction_type')
          .eq('listing_id', currentPropertyId)
          .maybeSingle();
        interests.push({
          property_id: currentPropertyId,
          property_title: propertyData?.title,
          transaction_type: propertyData?.transaction_type,
          created_at: (lead.createdAt || (lead as any).created_at || new Date().toISOString()).toString(),
          source: lead.source || 'portal'
        });
      }

      // 2) Adicionar imóveis registrados na timeline deste lead
      const { data: tlData } = await supabase
        .from('timeline_leads')
        .select('metadata, created_at, tipo')
        .eq('lead_id', lead.id)
        .not('metadata->>property_id', 'is', null);
      for (const entry of tlData || []) {
        const md = entry.metadata as any;
        const propId = md?.property_id as string | undefined;
        if (propId && !propertyIds.has(propId)) {
          propertyIds.add(propId);
          const { data: prop } = await supabase
            .from('properties')
            .select('title, transaction_type')
            .eq('listing_id', propId)
            .maybeSingle();
          interests.push({
            property_id: propId,
            property_title: prop?.title || md?.property_title,
            transaction_type: prop?.transaction_type,
            created_at: entry.created_at,
            source: md?.portal || 'portal'
          });
        }
      }

      // 3) Fallback: buscar leads_portais por email/telefone e mapear imóveis
      const portalRows: any[] = [];
      if (lead.email) {
        const { data: lpEmail } = await supabase
          .from('leads_portais')
          .select('client_listing_id, origin_listing_id, portal, created_at')
          .eq('email', lead.email)
          .order('created_at', { ascending: false })
          .limit(20);
        if (lpEmail) portalRows.push(...lpEmail);
      }
      if (lead.phone) {
        const { data: lpPhone } = await supabase
          .from('leads_portais')
          .select('client_listing_id, origin_listing_id, portal, created_at')
          .eq('phone', lead.phone)
          .order('created_at', { ascending: false })
          .limit(20);
        if (lpPhone) portalRows.push(...lpPhone);
      }

      // Preparar conjunto de listing_ids para buscar títulos em lote
      const listingIds: string[] = [];
      for (const r of portalRows) {
        const pid = r.client_listing_id || r.origin_listing_id;
        if (pid && !propertyIds.has(pid)) {
          propertyIds.add(pid);
          listingIds.push(pid);
        }
      }

      if (listingIds.length > 0) {
        const { data: propsBatch } = await supabase
          .from('properties')
          .select('listing_id, title, transaction_type')
          .in('listing_id', listingIds);
        const propsMap = new Map(propsBatch?.map(p => [p.listing_id, p]) || []);

        for (const r of portalRows) {
          const pid = r.client_listing_id || r.origin_listing_id;
          if (!pid) continue;
          const p = propsMap.get(pid);
          interests.push({
            property_id: pid,
            property_title: p?.title || pid,
            transaction_type: p?.transaction_type,
            created_at: r.created_at,
            source: r.portal || 'portal'
          });
        }
      }

      // Deduplicate property interests by property_id
      const uniqueInterests = interests.filter((interest, index, arr) => 
        arr.findIndex(i => i.property_id === interest.property_id) === index
      );

      uniqueInterests.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setPropertyInterests(uniqueInterests);
      console.log('🎯 Final property interests:', uniqueInterests);
    } catch (error) {
      console.error('❌ Erro ao carregar imóveis de interesse:', error);
    }
  };

  const getTimelineIcon = (tipo: string) => {
    switch (tipo) {
      case 'criacao':
        return <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>;
      case 'status_change':
        return <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>;
      case 'interesse_portal':
      case 'novo_interesse':
        return <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>;
      default:
        return <div className="w-2 h-2 bg-gray-400 rounded-full mt-2"></div>;
    }
  };

  const getTransactionTypeBadge = (transactionType: string) => {
    const type = transactionType?.toLowerCase();
    if (type === 'venda' || type === 'sale') {
      return <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Venda</span>;
    } else if (type === 'locacao' || type === 'locação' || type === 'rental' || type === 'rent') {
      return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Locação</span>;
    } else if (type === 'both' || type === 'venda/aluguel') {
      return <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">Venda/Aluguel</span>;
    }
    return <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
      {type === 'sale' ? 'Venda' : type === 'rent' ? 'Locação' : type === 'both' ? 'Venda/Aluguel' : transactionType}
    </span>;
  };

  const createPropertyLink = (propertyId: string, text: string) => {
    return (
      <a 
        href={`/imovel/${propertyId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 hover:underline"
      >
        {text}
      </a>
    );
  };

  const formattedDate = formatDate((lead.createdAt || (lead as any).created_at || new Date().toISOString()).toString());

  // Função para enviar email
  const handleSendEmail = () => {
    if (lead.email) {
      const subject = encodeURIComponent(`Contato sobre sua consulta - ${lead.name}`);
      const body = encodeURIComponent(`Olá ${lead.name},\n\nObrigado por entrar em contato conosco!\n\nAtenciosamente,\nEquipe Fox Vendas`);
      window.open(`mailto:${lead.email}?subject=${subject}&body=${body}`);
    }
  };

  // Função para fazer ligação
  const handleCall = () => {
    if (lead.phone) {
      // Limpar o número (remover caracteres especiais)
      const cleanPhone = lead.phone.replace(/\D/g, '');
      window.open(`tel:${cleanPhone}`);
    }
  };

  // Função para adicionar nota
  const handleAddNote = async () => {
    if (!noteText.trim()) {
      toast({
        title: "Erro",
        description: "Digite uma nota antes de salvar.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Buscar o nome do usuário logado
      const { data: { user } } = await supabase.auth.getUser();
      
      let userName = 'Usuário';
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('user_id', user.id)
          .single();
        
        if (profile) {
          userName = `${profile.first_name} ${profile.last_name}`.trim();
        }
      }

      const { error } = await supabase
        .from('timeline_leads')
        .insert([
          {
            lead_id: lead.id,
            titulo: 'Nota adicionada',
            descricao: noteText.trim(),
            tipo: 'nota',
            criado_por: userName,
            metadata: {}
          }
        ]);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Nota adicionada com sucesso!",
      });

      setNoteText('');
      setShowAddNote(false);
      // Recarregar timeline
      loadTimelineData();
    } catch (error) {
      console.error('Erro ao adicionar nota:', error);
      toast({
        title: "Erro",
        description: "Erro ao adicionar nota. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 p-2 rounded-full">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Detalhes do Lead</h3>
                  <p className="text-blue-100">{lead.name}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[80vh] overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Informações de Contato */}
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Mail className="w-5 h-5 mr-2 text-blue-500" />
                    Informações de Contato
                  </h4>
                  <div className="space-y-3">
                    {lead.email && (
                      <div className="flex items-center space-x-3">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-900">{lead.email}</span>
                      </div>
                    )}
                    {lead.phone && (
                      <div className="flex items-center space-x-3">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-900">{lead.phone}</span>
                      </div>
                    )}
                    {!lead.email && !lead.phone && (
                      <div className="text-center py-4">
                        <p className="text-gray-500">Nenhuma informação de contato disponível</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Tag className="w-5 h-5 mr-2 text-blue-500" />
                    Status e Origem
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(lead.status)}`}>
                        {translateStatus(lead.status)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Origem:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{getSourceIcon(lead.source)}</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSourceColor(lead.source)}`}>
                          {translateSource(lead.source)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Data de criação:</span>
                      <div className="text-right">
                        <div className="text-gray-900 flex items-center justify-end">
                          <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                          {formattedDate.date}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center justify-end">
                          <Clock className="w-3 h-3 mr-1" />
                          {formattedDate.time}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Imóveis de Interesse */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Globe className="w-5 h-5 mr-2 text-blue-500" />
                    Imóveis de Interesse
                  </h4>
                  {propertyInterests.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-gray-500">Nenhum imóvel de interesse registrado</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {propertyInterests.map((interest, index) => (
                        <div key={index} className="bg-white rounded-lg p-4 border">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                               <div className="flex items-center gap-2 mb-2">
                                 {createPropertyLink(interest.property_id, 
                                   interest.property_title 
                                     ? `${interest.property_title} (${interest.property_id})`
                                     : `Código: ${interest.property_id}`
                                 )}
                                 {interest.transaction_type && getTransactionTypeBadge(interest.transaction_type)}
                               </div>
                              <p className="text-sm text-gray-600">
                                Interesse demonstrado em {new Date(interest.created_at).toLocaleDateString('pt-BR')}
                              </p>
                              <p className="text-xs text-gray-500">
                                via {interest.source === 'Grupo OLX' ? 'Portal OLX' : interest.source}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Mensagem do Lead */}
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <MessageSquare className="w-5 h-5 mr-2 text-blue-500" />
                    Mensagem do Lead
                  </h4>
                  <div className="bg-white rounded-lg p-4 border">
                    <div className="prose max-w-none">
                      <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {lead.message}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Timeline/Histórico real */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Histórico</h4>
                  {loadingTimeline ? (
                    <p className="text-gray-500 text-sm">Carregando histórico...</p>
                  ) : timelineEntries.length === 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Lead criado</p>
                          <p className="text-xs text-gray-500">
                            {formattedDate.date} às {formattedDate.time}
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            via {translateSource(lead.source)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {timelineEntries.slice(0, 3).map((entry) => (
                        <div key={entry.id} className="flex items-start space-x-3">
                          {getTimelineIcon(entry.tipo)}
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{entry.titulo}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(entry.created_at).toLocaleString('pt-BR')}
                            </p>
                            {entry.descricao && (
                              <p className="text-xs text-gray-600 mt-1">{entry.descricao}</p>
                            )}
                            {entry.criado_por && (
                              <p className="text-xs text-gray-500 mt-1">Por: {entry.criado_por}</p>
                            )}
                            {/* Mostrar link do imóvel se houver */}
                            {(entry.metadata as any)?.property_id && (
                              <div className="mt-2 flex items-center gap-2">
                                {createPropertyLink(
                                  (entry.metadata as any).property_id,
                                  `Imóvel: ${(entry.metadata as any).property_title || (entry.metadata as any).property_id}`
                                )}
                                {(entry.metadata as any).transaction_type && 
                                  getTransactionTypeBadge((entry.metadata as any).transaction_type)
                                }
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {timelineEntries.length >= 5 && (
                        <button 
                          onClick={() => setShowTimeline(true)}
                          className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                        >
                          Ver histórico completo →
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Ações Rápidas */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h4>
                  <div className="grid grid-cols-2 gap-3">
                     {lead.email && (
                       <button 
                         onClick={handleSendEmail}
                         className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm flex items-center justify-center space-x-2"
                       >
                         <Mail className="w-4 h-4" />
                         <span>Enviar Email</span>
                       </button>
                     )}
                     {lead.phone && (
                       <button 
                         onClick={handleCall}
                         className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm flex items-center justify-center space-x-2"
                       >
                         <Phone className="w-4 h-4" />
                         <span>Ligar</span>
                       </button>
                     )}
                     <button 
                       onClick={() => setShowTimeline(true)}
                       className="bg-indigo-500 text-white px-4 py-2 rounded-lg hover:bg-indigo-600 transition-colors text-sm flex items-center justify-center space-x-2"
                     >
                       <History className="w-4 h-4" />
                       <span>Ver Timeline</span>
                     </button>
                     <button 
                       onClick={() => setShowAddNote(true)}
                       className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors text-sm flex items-center justify-center space-x-2"
                     >
                       <span>Adicionar Nota</span>
                     </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
      
      {/* Modal para Adicionar Nota */}
      {showAddNote && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowAddNote(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-6 py-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Adicionar Nota</h3>
                  <button
                    onClick={() => setShowAddNote(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="note-text" className="block text-sm font-medium text-gray-700 mb-2">
                    Nota sobre o lead
                  </label>
                  <textarea
                    id="note-text"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Digite sua nota sobre este lead..."
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowAddNote(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleAddNote}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    Salvar Nota
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timeline Modal */}
      <LeadTimelineModal
        isOpen={showTimeline}
        onClose={() => setShowTimeline(false)}
        leadId={originalLeadId || lead.id}
        leadName={lead.name}
      />
    </div>
  );
};

export default SimpleLeadDetailModal;